// ============================================
// TIER SWAP (CART PAGE ONLY) - DAWN
// Uses window.TIER_MAP built in sections/main-cart-items.liquid
// Tier rules encoded in SKU suffix: -T2 (2+), -T2-10 (2–10), etc.
// Race-condition safe checkout enforcement + button feedback.
// ============================================

(() => {
  function getTierMap() {
    return window.TIER_MAP || {};
  }
  
  // True if SKU ends with "-T<number>" or "-T<number>-<number>"
  function isTierSku(sku) {
    if (!sku) return false;
    const s = String(sku);
    const idx = s.lastIndexOf('-T');
    if (idx === -1) return false;

    const tail = s.slice(idx + 2);
    if (!tail) return false;

    const parts = tail.split('-');
    if (parts.length === 0 || parts.length > 2) return false;

    const min = Number(parts[0]);
    if (!Number.isFinite(min)) return false;

    if (parts.length === 2) {
      const max = Number(parts[1]);
      if (!Number.isFinite(max)) return false;
    }
    return true;
  }

  function getBaseSku(sku) {
    if (!sku) return sku;
    const s = String(sku);
    const idx = s.lastIndexOf('-T');
    if (idx === -1) return s;
    return isTierSku(s) ? s.slice(0, idx) : s;
  }

  function parseTierFromSku(sku) {
    if (!sku) return null;
    const s = String(sku);

    const idx = s.lastIndexOf('-T');
    if (idx === -1) return null;

    const tail = s.slice(idx + 2);
    const parts = tail.split('-');

    const min = Number(parts[0]);
    if (!Number.isFinite(min)) return null;

    let max = Infinity;
    if (parts.length > 1) {
      max = Number(parts[1]);
      if (!Number.isFinite(max) || max < min) return null;
    }

    return { min, max };
  }

  function pickDesiredVariantId(baseSku, qty) {
    const entry = getTierMap()[baseSku];
    if (!entry || !Array.isArray(entry.variants)) return null;

    const tiers = entry.variants
      .map(v => {
        if (!v || !v.sku) return null;
        const rule = parseTierFromSku(v.sku);
        if (!rule) return null;
        return { ...rule, variantId: Number(v.id) };
      })
      .filter(Boolean);

    const matches = tiers.filter(t => qty >= t.min && qty <= t.max);
    if (matches.length) {
      matches.sort((a, b) => b.min - a.min);
      return matches[0].variantId;
    }

    // fallback base
    const exactBase = entry.variants.find(v => v && v.sku === baseSku);
    if (exactBase) return Number(exactBase.id);

    const nonTier = entry.variants.find(v => v && v.sku && !isTierSku(v.sku));
    return nonTier ? Number(nonTier.id) : null;
  }

async function tierSwapNeeded() {
  const tierMap = getTierMap();
  const cart = await (await fetch('/cart.js', { credentials: 'same-origin' })).json();
  if (!cart.items || cart.items.length === 0) return false;

  const skuGroups = {};

  cart.items.forEach(item => {
    if (!item.sku) return;
    const baseSku = getBaseSku(item.sku);
    if (!tierMap[baseSku]) return;

    if (!skuGroups[baseSku]) skuGroups[baseSku] = { items: [], totalQty: 0 };
    skuGroups[baseSku].items.push(item);
    skuGroups[baseSku].totalQty += item.quantity;
  });

  for (const baseSku in skuGroups) {
    const group = skuGroups[baseSku];
    const desiredVariantId = pickDesiredVariantId(baseSku, group.totalQty);
    if (!desiredVariantId) continue;

    const allCorrect = group.items.every(it => Number(it.variant_id) === Number(desiredVariantId));
    if (!allCorrect) return true;
  }

  return false;
}

  async function removeLineByKey(lineKey) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`/cart/change.js failed (${res.status}): ${text}`);
    }
  }

  async function addVariant(variantId, quantity) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: Number(variantId), quantity: Number(quantity) })
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`/cart/add.js failed (${res.status}): ${text}`);
    }
  }

  async function waitForCartToSettle(timeoutMs = 2000) {
    const start = Date.now();
    let lastSig = null;

    while (Date.now() - start < timeoutMs) {
      const cart = await (await fetch('/cart.js', { credentials: 'same-origin' })).json();
      const sig = JSON.stringify((cart.items || []).map(i => [i.key, i.quantity, i.variant_id]).sort());

      if (sig === lastSig) return;
      lastSig = sig;

      await new Promise(r => setTimeout(r, 150));
    }
  }

  // ✅ NEW: button-only loading feedback
  function setCheckoutButtonLoading(btn, isLoading) {
    if (!btn) return;

    if (isLoading) {
      if (!btn.dataset.originalText) btn.dataset.originalText = (btn.textContent || '').trim();
      btn.textContent = 'Updating bulk pricing…';
      btn.setAttribute('aria-busy', 'true');

      // if it's a button
      if ('disabled' in btn) btn.disabled = true;

      btn.style.opacity = '0.65';
      btn.style.pointerEvents = 'none';
    } else {
      if (btn.dataset.originalText) {
        btn.textContent = btn.dataset.originalText;
        delete btn.dataset.originalText;
      }
      btn.removeAttribute('aria-busy');

      if ('disabled' in btn) btn.disabled = false;

      btn.style.opacity = '';
      btn.style.pointerEvents = '';
    }
  }

  let running = false;

  async function swapCartTiers({ reloadAfter = true } = {}) {
    if (running) return false;
    running = true;

    try {
      const tierMap = getTierMap();
      const cart = await (await fetch('/cart.js', { credentials: 'same-origin' })).json();
      if (!cart.items || cart.items.length === 0) return false;

      const skuGroups = {};

      cart.items.forEach(item => {
        if (!item.sku) return;

        const baseSku = getBaseSku(item.sku);
        if (!tierMap[baseSku]) return;

        if (!skuGroups[baseSku]) skuGroups[baseSku] = { items: [], totalQty: 0 };
        skuGroups[baseSku].items.push(item);
        skuGroups[baseSku].totalQty += item.quantity;
      });

      let changed = false;

      for (const baseSku in skuGroups) {
        const group = skuGroups[baseSku];
        const desiredVariantId = pickDesiredVariantId(baseSku, group.totalQty);
        if (!desiredVariantId) continue;

        const allCorrect = group.items.every(it => Number(it.variant_id) === Number(desiredVariantId));
        if (allCorrect) continue;

        changed = true;

        for (const it of group.items) {
          await removeLineByKey(it.key);
        }
        await addVariant(desiredVariantId, group.totalQty);
      }

      if (changed) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        if (reloadAfter) setTimeout(() => window.location.reload(), 250);
      }

      return changed;
    } catch (e) {
      console.error('[TierSwap] error:', e);
      return false;
    } finally {
      running = false;
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    if (!window.location.pathname.startsWith('/cart')) return;

    setTimeout(() => swapCartTiers({ reloadAfter: true }), 150);

    const cartContainer =
      document.querySelector('cart-items') ||
      document.querySelector('#main-cart-items') ||
      document.querySelector('.cart');

    if (cartContainer) {
      let t;
      new MutationObserver(() => {
        clearTimeout(t);
        t = setTimeout(() => swapCartTiers({ reloadAfter: true }), 250);
      }).observe(cartContainer, { childList: true, subtree: true });
    }

    // ✅ Checkout enforcement (same logic) + button feedback
    document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[name="checkout"], a[href^="/checkout"]');
  if (!btn) return;

  e.preventDefault();
  e.stopPropagation();

  let loadingShown = false;

  try {
    await waitForCartToSettle(800);

    const needsSwap = await tierSwapNeeded();

    if (needsSwap) {
      setCheckoutButtonLoading(btn, true);
      loadingShown = true;

      await swapCartTiers({ reloadAfter: false });
      await waitForCartToSettle(1200);
    }
  } catch (err) {
    console.error('[TierSwap] checkout enforcement failed:', err);
    if (loadingShown) setCheckoutButtonLoading(btn, false);
    return;
  }

  window.location.href = '/checkout';
}, true);
  });

  window.swapCartTiers = swapCartTiers;
  window.__tierDebug = { getTierMap, getBaseSku, parseTierFromSku, pickDesiredVariantId, isTierSku, waitForCartToSettle };
})();